/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { LexicalEditor } from 'lexical';

import { $createCodeNode, $isCodeNode } from '@lexical/code';
// import { exportFile, importFile } from '@lexical/file';

import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from '@lexical/markdown';
import { useCollaborationContext } from '@lexical/react/LexicalCollaborationContext';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { CONNECTED_COMMAND, TOGGLE_CONNECT_COMMAND } from '@lexical/yjs';
import {
  $createTextNode,
  $getRoot,
  $isParagraphNode,
  CLEAR_EDITOR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
} from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import useModal from '../../hooks/useModal';
import Button from '../../ui/Button';
import { PLAYGROUND_TRANSFORMERS } from '../MarkdownTransformers';
import {
  SPEECH_TO_TEXT_COMMAND,
  SUPPORT_SPEECH_RECOGNITION,
} from '../SpeechToTextPlugin';
import { $generateHtmlFromNodes } from '@lexical/html';
var fs = require('fs')

async function sendEditorState(editor: LexicalEditor): Promise<void> {
  const stringifiedEditorState = JSON.stringify(editor.getEditorState());
  try {
    await fetch('http://localhost:1235/setEditorState', {
      body: stringifiedEditorState,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      method: 'POST',
    });
  } catch {
    // NO-OP
  }
}

async function validateEditorState(editor: LexicalEditor): Promise<void> {
  const stringifiedEditorState = JSON.stringify(editor.getEditorState());
  let response = null;
  try {
    response = await fetch('http://localhost:1235/validateEditorState', {
      body: stringifiedEditorState,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      method: 'POST',
    });
  } catch {
    // NO-OP
  }
  if (response !== null && response.status === 403) {
    throw new Error(
      'Editor state validation failed! Server did not accept changes.',
    );
  }
}

export default function ActionsPlugin({
  isRichText,
}: {
  isRichText: boolean;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [isSpeechToText, setIsSpeechToText] = useState(false);
  const [connected, setConnected] = useState(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [modal, showModal] = useModal();
  const { isCollabActive } = useCollaborationContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      editor.registerCommand<boolean>(
        CONNECTED_COMMAND,
        (payload) => {
          const isConnected = payload;
          setConnected(isConnected);
          return false;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(
      ({ dirtyElements, prevEditorState, tags }) => {
        // If we are in read only mode, send the editor state
        // to server and ask for validation if possible.
        if (
          !isEditable &&
          dirtyElements.size > 0 &&
          !tags.has('historic') &&
          !tags.has('collaboration')
        ) {
          validateEditorState(editor);
        }
        editor.getEditorState().read(() => {
          const root = $getRoot();
          const children = root.getChildren();

          if (children.length > 1) {
            setIsEditorEmpty(false);
          } else {
            if ($isParagraphNode(children[0])) {
              const paragraphChildren = children[0].getChildren();
              setIsEditorEmpty(paragraphChildren.length === 0);
            } else {
              setIsEditorEmpty(false);
            }
          }
        });
      },
    );
  }, [editor, isEditable]);

  const handleMarkdownToggle = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      const firstChild = root.getFirstChild();
      if ($isCodeNode(firstChild) && firstChild?.getLanguage() === 'markdown') {
        $convertFromMarkdownString(
          firstChild.getTextContent(),
          PLAYGROUND_TRANSFORMERS,
        );
      } else {
        const markdown = $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);
        root
          .clear()
          .append(
            $createCodeNode('markdown').append($createTextNode(markdown)),
          );
      }
      root.selectEnd();
    });
  }, [editor]);

  const updateHtmlString = (htmlString: string) => {
    try {
      //@ts-ignore
      // let documentstyles = fs.readFileSync('../../themes/PlaygroundEditorTheme.css', 'utf8')
      let documentstyles = fs.readFileSync('../../themes/PlaygroundEditorTheme.css', 'utf8')
      const document = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            ${documentstyles}
          </style>
        </head>
        <body>
          ${htmlString}
        </body>
      </html>
    `;

      console.log(document);

      return document

    } catch (e: any) {
      console.log('error:', e.stack);
    }


  }


  const downloadPDF = () => {
    //@ts-ignore
    var doc = new jsPDF({
      orientation: 'portrait', // or 'landscape'
      unit: 'mm', // or 'pt', 'mm', 'cm', 'in'
      format: 'a4', // or other page formats
      putOnlyUsedFonts: true,
      compress: true,
      lineHeight: 1.2, // You can adjust this value as needed
      hotfixes: [],
      floatPrecision: 16, // or other precision value
      userUnit: 1.0,
      html2canvas: {
        width: 210, // Width of the container in mm (A4 size)
        scale: 1,   // Scale factor
      },
    });

    doc.fromHTML(document.getElementById('contentEditableId'), 5, 5, {
      width: 200, // Set the width to 100% of the page width (A4 in this case)
      elementHandlers: {},
    });

    doc.save('sample-file.pdf');

    // let doc: any = new jsPDF();
    // // var width = doc.internal.pageSize.getWidth();
    // // var height = doc.internal.pageSize.getHeight();
    // var option = {
    //   callback: function (doc: any) {
    //     console.log('sample.pdf')
    //     doc.save('sample.pdf');
    //   },
    //   margin: [60, 60, 60, 60],
    //   x: 10,
    //   y: 10,
    //   html2canvas: {
    //     scale: 0.5, //this was my solution, you have to adjust to your size
    //     width: 800 //for some reason width does nothing
    //   }

    // }
    // doc.html(`<p>THis is test example kavla testtttttttttttttttttt</p>`, option);
    // return
    // const doc = new jsPDF({
    //   orientation: 'p',
    //   unit: 'mm',
    //   format: 'a4',
    //   putOnlyUsedFonts: false
    // });
    // var option = {
    //   maxWidth: 500,
    //   width: 500,
    //   callback: function (doc: any) {
    //     console.log('sample.pdf')
    //     doc.save('sample.pdf');
    //   },
    // }
    // //@ts-ignore
    // const content = document.getElementById('contentEditableId').innerText;
    // // debugger;
    // doc.html(`<p>THis is test example kavla testtttttttttttttttttt</p>`, option);
    // doc.save('sample.pdf');

  };

  function downloadHTML(htmlString: any, filename = "downloaded.html") {
    // Create a blob of the HTML string
    let blob = new Blob([htmlString], { type: "text/html;charset=utf-8" });

    // Create a link element
    let a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;

    // Append the link to the document and simulate a click to download
    document.body.appendChild(a);
    a.click();

    // Remove the link from the document
    document.body.removeChild(a);
  }
  const htmlConvert = () => {
    editor.update(() => {
      const editorState = editor.getEditorState();
      const jsonString = JSON.stringify(editorState);
      console.log('jsonString', jsonString);

      const htmlString = $generateHtmlFromNodes(editor);
      let finalHtml: any = updateHtmlString(htmlString)
      downloadHTML(htmlString)
      console.log('htmlString', finalHtml);
    });
  }

  return (
    <div className="actions">
      {SUPPORT_SPEECH_RECOGNITION && (
        <button
          onClick={() => {
            editor.dispatchCommand(SPEECH_TO_TEXT_COMMAND, !isSpeechToText);
            setIsSpeechToText(!isSpeechToText);
          }}
          className={
            'action-button action-button-mic ' +
            (isSpeechToText ? 'active' : '')
          }
          title="Speech To Text"
          aria-label={`${isSpeechToText ? 'Enable' : 'Disable'
            } speech to text`}>
          <i className="mic" />
        </button>
      )}
      {/* <button
        className="action-button import"
        onClick={() => importFile(editor)}
        title="Import"
        aria-label="Import editor state from JSON">
        <i className="import" />
      </button>*/}
      <button
        className="action-button export"
        onClick={() => { downloadPDF() }
          // exportFile(editor, {
          //   fileName: `Playground ${new Date().toISOString()}`,
          //   source: 'Playground',
          // })
        }
        title="Export"
        aria-label="Export editor state to JSON">
        {/* <i className="export" /> */}
        <i className="pdfIcon" />
      </button>
      <button
        className="action-button export"
        onClick={() => { htmlConvert() }
        }
        title="Export"
        aria-label="Export editor state to JSON">
        <i className="htmlIcon" />
      </button>
      {/* <button
        className="action-button clear"
        disabled={isEditorEmpty}
        onClick={() => {
          showModal('Clear editor', (onClose) => (
            <ShowClearDialog editor={editor} onClose={onClose} />
          ));
        }}
        title="Clear"
        aria-label="Clear editor contents">
        <i className="clear" />
      </button> */}
      <button
        className={`action-button ${!isEditable ? 'unlock' : 'lock'}`}
        onClick={() => {
          // Send latest editor state to commenting validation server
          if (isEditable) {
            sendEditorState(editor);
          }
          editor.setEditable(!editor.isEditable());
        }}
        title="Read-Only Mode"
        aria-label={`${!isEditable ? 'Unlock' : 'Lock'} read-only mode`}>
        <i className={!isEditable ? 'unlock' : 'lock'} />
      </button>
      {/* <button
        className="action-button"
        onClick={handleMarkdownToggle}
        title="Convert From Markdown"
        aria-label="Convert from markdown">
        <i className="markdown" />
      </button> */}
      {isCollabActive && (
        <button
          className="action-button connect"
          onClick={() => {
            editor.dispatchCommand(TOGGLE_CONNECT_COMMAND, !connected);
          }}
          title={`${connected ? 'Disconnect' : 'Connect'
            } Collaborative Editing`}
          aria-label={`${connected ? 'Disconnect from' : 'Connect to'
            } a collaborative editing server`}>
          <i className={connected ? 'disconnect' : 'connect'} />
        </button>
      )}
      {modal}
    </div>
  );
}

function ShowClearDialog({
  editor,
  onClose,
}: {
  editor: LexicalEditor;
  onClose: () => void;
}): JSX.Element {
  return (
    <>
      Are you sure you want to clear the editor?
      <div className="Modal__content">
        <Button
          onClick={() => {
            editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
            editor.focus();
            onClose();
          }}>
          Clear
        </Button>{' '}
        <Button
          onClick={() => {
            editor.focus();
            onClose();
          }}>
          Cancel
        </Button>
      </div>
    </>
  );
}
